import React from 'react';
import { cn } from '../../lib/utils';

export interface GrowthIndicatorProps {
  value: number;
  showZero?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * GrowthIndicator component displays percentage growth with appropriate styling
 * Follows AGENTS.md principles for reusable UI components
 */
export function GrowthIndicator({ 
  value, 
  showZero = false, 
  size = 'sm',
  className 
}: GrowthIndicatorProps) {
  // Don't render if value is 0 and showZero is false
  if (value === 0 && !showZero) {
    return null;
  }

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const getVariantClasses = (value: number) => {
    if (value > 0) {
      return 'text-green-700 bg-green-100 border-green-200';
    } else if (value < 0) {
      return 'text-red-700 bg-red-100 border-red-200';
    } else {
      return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const formatValue = (value: number) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value}%`;
  };

  return (
    <span 
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        sizeClasses[size],
        getVariantClasses(value),
        className
      )}
      title={`Growth: ${formatValue(value)}`}
    >
      {formatValue(value)}
    </span>
  );
}

GrowthIndicator.displayName = 'GrowthIndicator';